import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDragLayer } from 'react-dnd';
import OrganizerRow from './OrganizerRow';
import { OrganizerVerticalDropZone } from './OrganizerDropZone';
import OrganizerDragPreview from './OrganizerDragPreview';
import { Icon } from '../../../../ui';
import theme from '../../../../ui/theme';
import { ItemTypes } from '../../utils/dragTypes';

const SCROLL_ZONE_SIZE = 60;
const SCROLL_SPEED = 10;
const DROP_HIGHLIGHT_DURATION = 800; // ms to show highlight after drop

// Main content container
const OrganizerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;
  min-height: 200px;
  max-width: 600;
  opacity: ${({ $isSaving }) => ($isSaving ? 0.6 : 1)};
  pointer-events: ${({ $isSaving }) => ($isSaving ? 'none' : 'auto')};
  transition: opacity 0.2s ease;
`;

// Empty state
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: ${theme.colors.NEU_600};
`;

const EmptyStateText = styled.p`
  margin: 12px 0 0;
  font-size: 14px;
  color: ${theme.colors.NEU_600};
`;

/**
 * LayoutOrganizerContent - Main content area for the layout organizer modal
 */
const LayoutOrganizerContent = ({ localRows, onDrop, themeColor, isSaving }) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [draggedModule, setDraggedModule] = useState(null);
  const [recentlyDroppedId, setRecentlyDroppedId] = useState(null);
  const containerRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  // Monitor global drag state
  const {
    isDragging: globalIsDragging,
    item,
    currentOffset,
  } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging() && monitor.getItemType() === ItemTypes.MODULE,
    item: monitor.getItem(),
    currentOffset: monitor.getClientOffset(),
  }));

  const prevGlobalIsDraggingRef = useRef(false);

  // Find the row index and module count for the dragged module
  const draggedModuleInfo = useMemo(() => {
    if (!draggedModule) return null;

    const rowIndex = localRows.findIndex((row) => row.pageModules?.some((m) => m.id === draggedModule.moduleId));

    if (rowIndex === -1) return null;

    const moduleCount = localRows[rowIndex].pageModules?.length || 0;

    return { rowIndex, moduleCount };
  }, [draggedModule, localRows]);

  // Determine if a vertical drop zone position is valid
  const isVerticalDropZoneValid = useCallback(
    (position) => {
      if (!isDragging || !draggedModuleInfo) return true;

      const { rowIndex, moduleCount } = draggedModuleInfo;

      // If the module is alone in its row, dropping above or below keeps it in the same place
      if (moduleCount === 1) {
        // Position equals rowIndex means "above this row"
        // Position equals rowIndex + 1 means "below this row"
        if (position === rowIndex || position === rowIndex + 1) {
          return false;
        }
      }

      return true;
    },
    [isDragging, draggedModuleInfo],
  );

  // Auto-scroll when dragging near edges
  useEffect(() => {
    if (!isDragging || !currentOffset || !containerRef.current) {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      return;
    }

    const scrollableParent = containerRef.current.closest('.MuiDialogContent-root');
    if (!scrollableParent) return;

    const rect = scrollableParent.getBoundingClientRect();
    const { y } = currentOffset;
    const distanceFromTop = y - rect.top;
    const distanceFromBottom = rect.bottom - y;

    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }

    if (distanceFromTop < SCROLL_ZONE_SIZE && scrollableParent.scrollTop > 0) {
      const scrollAmount = Math.max(1, SCROLL_SPEED * (1 - distanceFromTop / SCROLL_ZONE_SIZE));
      scrollIntervalRef.current = setInterval(() => {
        scrollableParent.scrollTop -= scrollAmount;
      }, 16);
    } else if (distanceFromBottom < SCROLL_ZONE_SIZE) {
      const maxScroll = scrollableParent.scrollHeight - scrollableParent.clientHeight;
      if (scrollableParent.scrollTop < maxScroll) {
        const scrollAmount = Math.max(1, SCROLL_SPEED * (1 - distanceFromBottom / SCROLL_ZONE_SIZE));
        scrollIntervalRef.current = setInterval(() => {
          scrollableParent.scrollTop += scrollAmount;
        }, 16);
      }
    }

    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };
  }, [isDragging, currentOffset]);

  // Sync local state with global drag layer
  useEffect(() => {
    if (globalIsDragging && !isDragging) {
      setIsDragging(true);
      setDraggedModule(item);
    } else if (!globalIsDragging && isDragging) {
      setIsDragging(false);
      setDraggedModule(null);
    } else if (globalIsDragging && item) {
      setDraggedModule(item);
    }

    if (prevGlobalIsDraggingRef.current && !globalIsDragging && isDragging) {
      setIsDragging(false);
      setDraggedModule(null);
    }

    prevGlobalIsDraggingRef.current = globalIsDragging;
  }, [globalIsDragging, isDragging, item]);

  // Handle drop with animation
  const handleDrop = useCallback(
    (dropData) => {
      // Highlight the dropped module
      setRecentlyDroppedId(dropData.moduleId);
      setTimeout(() => setRecentlyDroppedId(null), DROP_HIGHLIGHT_DURATION);

      // Call parent handler
      onDrop(dropData);
    },
    [onDrop],
  );

  const hasModules = localRows.some((row) => row.pageModules?.length > 0);

  if (!hasModules) {
    return (
      <EmptyState>
        <Icon.ViewModule size='large' color={theme.colors.NEU_400} />
        <EmptyStateText>{t('editCourse.layoutOrganizerEmpty')}</EmptyStateText>
      </EmptyState>
    );
  }

  return (
    <>
      <OrganizerContainer ref={containerRef} $isSaving={isSaving}>
        <OrganizerVerticalDropZone position={0} onDrop={handleDrop} isVisible={isDragging && isVerticalDropZoneValid(0)} />

        {localRows.map((row, index) => (
          <React.Fragment key={row.id}>
            <OrganizerRow
              row={row}
              onDrop={handleDrop}
              isDragging={isDragging}
              draggedModule={draggedModule}
              themeColor={themeColor}
              recentlyDroppedId={recentlyDroppedId}
            />
            <OrganizerVerticalDropZone position={index + 1} onDrop={handleDrop} isVisible={isDragging && isVerticalDropZoneValid(index + 1)} />
          </React.Fragment>
        ))}
      </OrganizerContainer>

      {isDragging && draggedModule && currentOffset && (
        <OrganizerDragPreview moduleType={draggedModule.moduleType} x={currentOffset.x} y={currentOffset.y} themeColor={themeColor} />
      )}
    </>
  );
};

export default LayoutOrganizerContent;
